import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Tabs, Tab, Grid, Divider } from "@mui/material";
import { UseIsAdmin } from "../../hooks/auth";
import { UseLeaderboardGroupKpis } from "../../hooks/kpi";
import moment from "moment";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import TextField from "@mui/material/TextField";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { ReactComponent as LeaderboardIcon } from "../../img/chartIcon.svg";

import SoftBox from "../SoftBox";
import LightSelect from "../../pages/CompanyView/LightSelect";
import LiveLeaderboard from "./LiveLeaderboard";
import PastLeaderboard from "./PastLeaderboard";
import SoftButton from "../SoftButton";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SoftTypography from "../SoftTypography";
import { UseSelectedWorkspace, UseCompanyColors } from "../../hooks/company";

const CalendarOpenPickerIcon = () => (
  <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9.39092 10.6855H10.6862V14.8953M3.23824 7.77111H17.4866M13.6007 5.18051V2.5899M7.12414 5.18051V2.5899M4.53354 18.1335H16.1913C16.5348 18.1335 16.8643 17.9971 17.1072 17.7541C17.3501 17.5112 17.4866 17.1818 17.4866 16.8382V5.18051C17.4866 4.83697 17.3501 4.50751 17.1072 4.26459C16.8643 4.02168 16.5348 3.88521 16.1913 3.88521H4.53354C4.19 3.88521 3.86054 4.02168 3.61762 4.26459C3.3747 4.50751 3.23824 4.83697 3.23824 5.18051V16.8382C3.23824 17.1818 3.3747 17.5112 3.61762 17.7541C3.86054 17.9971 4.19 18.1335 4.53354 18.1335Z" stroke="#667085" strokeWidth="1.5997" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);


// section: "full" (list + podium, the classic board) | "list" | "podium" —
// the customizable dashboard renders the two halves as independent blocks.
// periodIdx: when provided (the dashboard's global period filter), it drives
// the board instead of the internal period toggle.
function LeaderBoard({
  isViewOnly = false,
  bigMode = true,
  isRealAdmin = false,
  section = "full",
  periodIdx = null,
  dashboard = false,
  title,
  /* Dashboard global date window — forwarded so LiveLeaderboard can clip
     custom/quarter/YTD without swapping the ProgressBar UI for LeaderboardTable. */
  boardWindow = null,
  boardCustom = null,
  workspaceId = null,
}) {
  const workspace = UseSelectedWorkspace();
  const [leaderboardTab, setLeaderboardTab] = useState(0);
  const [togglePeriod, setTogglePeriod] = useState(3);
  const [toggleKpi, setToggleKpi] = useState('default');
  // Scorekeeper-only metrics must not appear in public leaderboard filters.
  const myKpis = UseLeaderboardGroupKpis();
  const isAdmin = UseIsAdmin();
  const navigate = useNavigate();
  const breakpointTheme = useTheme();
  const isBigScreen = useMediaQuery(breakpointTheme.breakpoints.up("lg"));
  const isSmallScreen = useMediaQuery(breakpointTheme.breakpoints.down("sm"));
  const isMidsizeScreen = useMediaQuery(breakpointTheme.breakpoints.down("md"));
  const screenWidth = window.innerWidth;

  const [isToggle, setisToggle] = useState(false);

  // On the mobile dashboard stack this widget has no scroll container of its
  // own (the page itself scrolls) — the sticky header below is disabled for
  // exactly this combination, so it's safe to swap the outer card's
  // `overflow: clip` for `hidden` here without breaking sticky-while-scrolling
  // elsewhere. It matters because `clip` support is inconsistent on mobile
  // browsers; where it's a no-op, the flush white header paints straight over
  // (rather than being clipped to) the card's rounded top corners.
  const isMobileDashboardList = section === "list" && dashboard && !isBigScreen;

  const togglePeriodNames = [
    { key: 0, label: "Yesterday" },
    { key: 1, label: "Today" },
    { key: 2, label: "This Week" },
    { key: 4, label: "Last Week" },
    { key: 3, label: "This Month" },
  ];

  const [monthRef, setMonthRef] = React.useState(
    moment().subtract(1, "months"),
  );

  // The dashboard's global period filter wins over the internal toggle.
  const activePeriod = periodIdx ?? togglePeriod;

  const LeaderboardComponent = [
    <LiveLeaderboard
      periodIdx={activePeriod}
      kpiId={toggleKpi}
      bigMode={isBigScreen}
      isAdmin={isAdmin}
      isViewOnly
      section={section}
      boardWindow={boardWindow}
      boardCustom={boardCustom}
      workspaceId={workspaceId}
    />,
    <PastLeaderboard bigMode={isBigScreen} isAdmin={isAdmin} monthRef={monthRef} />,
  ];

  const kpiOptions = [
    { value: 'default', label: 'All KPI' },
    ...(myKpis?.map((kpi) => ({
      value: kpi._id,
      label: kpi.title,
    })) || [])
  ];

  const { primaryColor } = UseCompanyColors();

  // Standalone podium block — the card shell + top three. No title: the
  // podium speaks for itself.
  if (section === "podium") {
    return (
      <SoftBox
        borderRadius="12px"
        sx={{
          background: "var(--tvd-card, #fff)",
          // Below lg the dashboard stacks and cells have no fixed height —
          // give the podium a sane stage instead of letting it balloon.
          height: { mini: "440px", lg: "100%" },
          display: "flex",
          flexDirection: "column",
          // SoftBox marginX on Homepage owns page gutters when dashboard.
          // Standalone Company View still supplies its own mini/sm insets.
          marginLeft: dashboard ? 0 : { mini: "1rem", sm: "1.5rem", lg: 0 },
          marginRight: dashboard ? 0 : { mini: "1rem", sm: "1.5rem", lg: 0 },
          // Slim paddings — give the podium the whole canvas.
          paddingX: { mini: "0.5rem", xs: "0.75rem", md: "0.75rem" },
          paddingY: { mini: "0.75rem", lg: "0.75rem" },
        }}
      >
        <LiveLeaderboard
          periodIdx={activePeriod}
          kpiId={toggleKpi}
          bigMode={isBigScreen}
          isAdmin={isAdmin}
          isViewOnly
          section="podium"
          boardWindow={boardWindow}
          boardCustom={boardCustom}
          workspaceId={workspaceId}
        />
      </SoftBox>
    );
  }

  return (
    <SoftBox
      borderRadius="12px"
      sx={{
        marginTop: {
          mini: dashboard || section === "list" ? 0 : ".40rem",
          lg: section === "list" ? 0 : ".40rem",
        },
        background: "var(--tvd-card, #fff)",
        // SoftBox marginX on Homepage owns gutters when dashboard; keep
        // standalone Company View mini/sm insets.
        marginLeft: dashboard ? 0 : { mini: "1rem", sm: "1.5rem", lg: 0 },
        marginRight: dashboard ? 0 : { mini: "1rem", sm: "1.5rem", lg: 0 },
        paddingX: {
          mini: "0.5rem",
          xs: "1rem",
          md: "2rem",
        },
        // Standalone list block: zero top/bottom padding so the sticky
        // header's resting position equals its pinned position (no jump on
        // scroll). 20px side padding = the same title inset as other cards.
        paddingY: section === "list" ? 0 : { mini: "1rem", lg: "1.5rem" },
        // paddingBottom reserves the pinned pager's lane — on tablet the card
        // hugs its content, so without it the pager overlaps the last row.
        ...(section === "list" && { minHeight: "100%", paddingX: "20px", position: "relative", paddingBottom: "52px" }),
        // overflow:clip trims child content at the border-radius without
        // creating a scroll container (unlike overflow:hidden), so the sticky
        // header above still works. On the mobile dashboard list card sticky
        // is already off (see isMobileDashboardList below), so hidden is used
        // there instead for more reliable corner-clipping on mobile browsers.
        overflow: isMobileDashboardList ? "hidden" : "clip",
      }}
    >
      <SoftBox
        sx={{
          width: "100%",
          minHeight: "25vh",
          padding: "16px 0",
          ...(isSmallScreen && ({ padding: "8px 8px" })),
          ...(section === "list" && { padding: "0" }),
        }}
      >
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={isBigScreen ? 0 : 2}
          sx={section !== "podium" ? {
            background: "var(--tvd-card, #fff)",
            // Match the card's own 12px radius — this header sits flush
            // (zero padding above it) against the card's rounded top edge,
            // so it needs its own matching corners. Relying solely on the
            // card's `overflow: clip` to carve them isn't safe here: `clip`
            // has spottier mobile-browser support than `hidden`, and on any
            // browser where it's a no-op this opaque, square-cornered header
            // simply paints straight over the card's rounded top corners.
            borderTopLeftRadius: "12px",
            borderTopRightRadius: "12px",
            paddingTop: "17px",
            paddingBottom: "14px",
            rowGap: "12px",
            borderBottom: "1px solid var(--tvd-hair, #EAECF0)",
            // Sticky only has a proper containing block where this widget owns
            // its own internal scroll (the desktop dashboard grid cell, via
            // .gmbw-scroll/.gmbw-fill in DashboardCanvas). On the mobile
            // dashboard stack the page itself scrolls and each card sits in
            // normal flow with no scroll container of its own — sticky there
            // detaches from its card on scroll and pins to the viewport,
            // visually overlapping whatever card is stacked above it.
            ...(!isMobileDashboardList && { position: "sticky", top: 0, zIndex: 20 }),
          } : undefined}
        >
          <Grid item mini={12} sx={{ height: { mini: "40px", sm: "unset" }, ...(screenWidth === 1024 && ({ height: "30px!important" })) }}>
            <SoftBox
              width="100%"
              sx={section === "list" ? { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" } : undefined}
            >
              <SoftTypography
                sx={{
                  // Section title — unified dashboard header token: 18/600.
                  fontFamily: "DM Sans",
                  textFillColor: "var(--tvd-ink, #0F172A)",
                  color: "var(--tvd-ink, #0F172A)",
                  fontSize: "16px",
                  lineHeight: 1.2,
                  fontWeight: 600,
                  letterSpacing: "-0.01em",
                  marginBottom: section === "list" ? 0 : "2rem",
                }}
              >
                {title || `${workspace.name} Leaderboard`}
              </SoftTypography>
            </SoftBox>
          </Grid>
          <Grid
            item
            mini={section === "list" ? "auto" : 12}
            xs={section === "list" ? "auto" : 8}
            lg={section === "list" ? "auto" : 4}
            xxl={section === "list" ? "auto" : 4}
            xxxl={section === "list" ? "auto" : 4}
            sx={{ display: "flex", gap: "0.5rem" }}
          >
            <SoftBox
              shadow={section === "list" ? "none" : "sm"}
              borderRadius="8px"
              width={section === "list" ? "auto" : "325px"}
              sx={section === "list" ? { border: "1px solid var(--tvd-hair, #EAECF0)", boxShadow: "none" } : undefined}
            >
              <Tabs
                value={leaderboardTab}
                onChange={(e, value) => {
                  setLeaderboardTab(value);
                  setisToggle(false);
                }}
                sx={{
                  padding: { mini: "0.25rem", md: "0.5rem" },
                  width: "100%",
                  "& .MuiTabs-indicator": {
                    borderRadius: "7px",
                    boxShadow: "none",
                  },
                  
                }}
              >
                <Tab
                  icon={
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10 2.29166C10.275 2.29166 10.55 2.39166 10.7667 2.58333C11.5084 3.24166 12.725 4.51665 13.5584 6.29165C13.7334 6.66665 13.8917 7.05832 14.025 7.47499C14.1334 7.82499 14.225 8.17498 14.3 8.53332C14.6917 10.4583 14.2584 12.45 13.65 14.2833C13.4167 15 13.2334 15.825 12.8084 16.4583C12.7834 16.4917 12.7667 16.525 12.75 16.55C12.525 16.85 12.175 17.025 11.8 17.025C11.8 17.025 10.9 17.025 10 17.025C9.10003 17.025 8.20002 17.025 8.20002 17.025C7.82502 17.025 7.47503 16.85 7.25003 16.55C7.23337 16.5167 7.21669 16.4917 7.19169 16.4583C6.76669 15.825 6.58335 15.0083 6.35002 14.2833C5.75002 12.45 5.30835 10.4583 5.70002 8.53332C5.77502 8.17498 5.8667 7.82499 5.97503 7.47499C6.10837 7.05832 6.2667 6.66665 6.4417 6.29165C7.27503 4.51665 8.4917 3.25 9.23337 2.58333C9.45003 2.39166 9.72503 2.29166 10 2.29166Z" stroke={leaderboardTab === 1 ? primaryColor : "white"} strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M12.8083 16.4583L14.3 17.5583C14.525 17.725 14.8 17.7583 15.0583 17.6666C15.1667 17.6166 15.2583 17.5583 15.3333 17.4833C15.4417 17.375 15.5167 17.2416 15.55 17.0833L16.0833 14.4416C16.1583 14.0833 16.0583 13.7333 15.8333 13.4583L14.4084 11.8C14.4167 11.7833 14.4167 11.7666 14.4167 11.75" stroke={leaderboardTab === 1 ? primaryColor : "white"} strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M5.5833 11.7917L4.15828 13.45C3.93328 13.725 3.83329 14.0834 3.90829 14.4334L4.44161 17.075C4.49161 17.3417 4.6833 17.5584 4.9333 17.6584C5.19163 17.75 5.46661 17.7167 5.69161 17.55L7.18328 16.45" stroke={leaderboardTab === 1 ? primaryColor : "white"} strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M9.99998 9.82497C10.9112 9.82497 11.65 9.08622 11.65 8.17497C11.65 7.2637 10.9112 6.52496 9.99998 6.52496C9.08873 6.52496 8.34998 7.2637 8.34998 8.17497C8.34998 9.08622 9.08873 9.82497 9.99998 9.82497Z" stroke={leaderboardTab === 1 ? primaryColor : "white"} strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M6.69995 13.95H13.2999" stroke={leaderboardTab === 1 ? primaryColor : "white"} strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  }
                  label="Leaderboard"
                  sx={{ fontFamily: "DM Sans", fontSize: "13px", fontWeight: 500 }}
                />
                <Tab
                  icon={<LeaderboardIcon sx={{ height: "1.5rem" }} />}
                  label="Past Boards"
                  sx={{ fontFamily: "DM Sans", fontSize: "13px", fontWeight: 500 }}
                />
              </Tabs>
            </SoftBox>
          </Grid>

          {!isViewOnly && !isSmallScreen && (
            <Grid
              item
              mini={section === "list" ? true : 6}
              lg={section === "list" ? true : 8}
              xxl={section === "list" ? true : 8}
              xxxl={section === "list" ? true : 8}
              display="flex"
              justifyContent="end"
              alignItems="center"
              gap={isMidsizeScreen ? "8px" : "1rem"}
            >
              {/* Calendar button for desktop view */}
              {leaderboardTab === 0 && (
                <SoftBox
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  sx={{maxWidth: isMidsizeScreen ? "260px!important" : "260px!important"}}
                >
                  {section !== "list" && (
                    <SoftTypography sx={{fontSize: "12px", fontWeight: "500", color: "var(--tvd-body, #667085)", marginRight: "0.5rem"}}>Filter by KPI:</SoftTypography>
                  )}
                  {/* Light frosted, portalled menu — never dark, never clipped
                      by the card (the old SoftSelect menu was both). */}
                  <LightSelect
                    options={kpiOptions}
                    value={toggleKpi}
                    onChange={(option) => setToggleKpi(option.value)}
                    width={isMidsizeScreen ? 130 : 140}
                    height={36}
                    fontSize={12}
                    radius={8}
                  />
                </SoftBox>
              )}
              <SoftBox
                display="flex"
                alignItems="center"
                justifyContent="center"
                sx={{maxWidth: leaderboardTab === 0 ? "150px!important": "160px!important"}}
              >
                {/* <SoftSelect
                  id="periods"
                  name="periods"
                  options={PERIODS}
                  onChange={(option) => setTogglePeriod(option.value)}
                  defaultValue={PERIODS[3]}
                /> */}
                {leaderboardTab === 0 && section !== "list" && (
                  <SoftButton
                    onClick={() => setisToggle(prev => !prev)}
                    sx={{
                      background: "var(--tvd-card, #FFFFFF)",
                      border: "1px solid rgba(102, 112, 133, 0.2)",
                      borderRadius: "4px!important",
                      fontFamily: 'DM Sans',
                      fontWeight: 600,
                      fontSize: "12px",
                      lineHeight: "18px",
                      textAlign: "center",
                      color: "primary",
                      textTransform: "capitalize!important",
                      paddingY: "0.5rem!important",
                      boxShadow: "none!important"
                    }}
                  >
                    <SoftBox display="flex" justifyContent="space-between" alignContent="center" gap="1rem">
                    {togglePeriodNames.find(item => item.key === togglePeriod)?.label}
                    <svg
                      style={{
                          fill: "#67748e",
                          width: "14px",
                          height: "14px",
                          strokeWidth: 1,
                          stroke: "#67748e",
                          marginRight: "-0.8rem",
                          marginTop: "0.05rem", 
                          transform: isToggle ? "rotate(180deg)" : "none"
                      }}
                      height={20}
                      width={20}
                      viewBox="0 0 20 20"
                      aria-hidden="true"
                      focusable="false"
                      className="css-tj5bde-Svg"
                    >
                      <path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z" />
                    </svg>
                    </SoftBox>
                  </SoftButton>
                )}
                {leaderboardTab === 1 && (
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                      openTo="month"
                      views={["year", "month"]}
                      inputFormat="MM/YYYY"
                      mask="__/____"
                      value={monthRef}
                      onChange={(newValue) => setMonthRef(newValue)}
                      components={{
                        OpenPickerIcon: CalendarOpenPickerIcon,
                      }}
                      PopperProps={{
                      sx: {
                        '& .MuiPaper-root': {
                          borderRadius: "11.2px",
                          maxWidth: "190px",
                          width: "190px",
                          marginTop: "1rem",
                          marginLeft: "4rem"
                        },
                        '& .MuiYearPicker-root': {
                          borderRadius: "11.2px",
                          maxWidth: "190px",
                          width: "190px",
                        },
                        '& .PrivatePickersYear-yearButton': {
                          fontFamily: 'DM Sans',
                          fontWeight: 600,
                          fontSize: "12px",
                          lineHeight: "18px",
                          letterSpacing: "-0.02em",
                          height: "auto !important",
                          width: "fit-content !important",
                          maxWidth: "none !important",
                          paddingRight: "6px",
                          paddingLeft: "6px"
                        },
                        '& .PrivatePickersYear-selected': {
                          backgroundColor: `${primaryColor} !important`,
                        },
                        '& .MuiMonthPicker-root': {
                          maxWidth: "180px",
                          width: "180px",
                        },
                        '& .MuiTypography-subtitle1': {
                          fontFamily: 'DM Sans',
                          fontWeight: 600,
                          fontSize: "12px",
                          lineHeight: "18px",
                          letterSpacing: "-0.02em",
                          height: "auto !important",
                          width: "fit-content !important",
                          maxWidth: "none !important"
                        },
                        '& .MuiTypography-h5': {
                          fontFamily: 'DM Sans',
                          fontWeight: 600,
                          fontSize: "12px",
                          lineHeight: "18px",
                          letterSpacing: "-0.02em",
                          width: "fit-content !important",
                          maxWidth: "none !important",
                          backgroundColor: `${primaryColor} !important`,
                          height: "auto !important"
                        },
                      },
                    }}
                      slotProps={{
                        textField: {
                          helperText: null,
                          label: undefined,
                          sx: {
                            width: "10rem",
                            "& .MuiInputBase-root": {
                              borderRadius: "4px!important",
                            },
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "4px!important",
                            },
                            "& .MuiInputBase-input": {
                              fontFamily: 'DM Sans',
                              fontWeight: 600,
                              fontSize: "12px",
                              lineHeight: "18px",
                              letterSpacing: "-0.02em",
                              color: "#667085",
                            },
                            "& .MuiFormLabel-root": {
                              fontFamily: 'DM Sans',
                              fontWeight: 600,
                              fontSize: "12px",
                              lineHeight: "18px",
                              letterSpacing: "-0.02em",
                              color: "#667085",
                            },
                            "& svg": {
                              width: "21px",
                              height: "21px",
                              marginRight: "-1rem!important"
                            },
                          }
                        },
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          helperText={null}
                          sx={{
                            width: "10rem",
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "4px!important",
                            },
                            "& .MuiInputBase-input": {
                              fontFamily: 'DM Sans',
                              fontWeight: 600,
                              fontSize: "12px",
                              lineHeight: "18px",
                              letterSpacing: "-0.02em",
                              color: "dark.main",
                            },
                            "& .MuiFormLabel-root": {
                              fontFamily: 'DM Sans',
                              fontWeight: 600,
                              fontSize: "12px",
                              lineHeight: "18px",
                              letterSpacing: "-0.02em",
                              color: "#667085",
                            },
                            "& svg": {
                              width: "21px",
                              height: "21px",
                              marginRight: "-1rem!important"
                            },
                          }}
                        />
                      )}
                    />
                  </LocalizationProvider>
                )}
                {isToggle && section !== "list" && (
                  <SoftBox
                    sx={{
                      marginTop: "16rem",
                      marginLeft: "3rem",
                      zIndex: "999",
                      background: "var(--tvd-card, #FFFFFF)!important", 
                      maxWidth: "193px", 
                      width: "193px", 
                      paddingX: "0.5rem",
                      paddingY: "0.25rem", 
                      position: "absolute",
                      border: "1.3px solid rgba(0, 0, 0, 0.1)",
                      boxShadow: "0px 20.7px 31px rgba(0, 0, 0, 0.1)",
                      borderRadius: "10.4px",
                    }}>
                      <SoftTypography
                        sx={{
                          fontStyle: "normal",
                          fontWeight: 500,
                          fontSize: "10.4px",
                          lineHeight: "23px",
                          color: "#667085",
                        }}>
                        Quick date filter
                      </SoftTypography>
                      <ul style={{ fontSize: "15.5px", color: "#667085", marginBottom: "1rem" }}>
                        {togglePeriodNames.map((item) => (
                          <li
                            key={item.key}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.5rem",
                              padding: "0.5rem",
                              cursor: "pointer",
                              background: togglePeriod === item.key ? "#EAECF0" : "transparent",
                              borderRadius: "6px",
                              borderBottom: togglePeriod === item.key ? "0.4px solid #98A2B3" : "none!important"

                            }}
                            onClick={() => {
                              setTogglePeriod(item.key);
                              setisToggle(false);
                            }}
                          >
                            {togglePeriod === item.key ? (
                              <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="6.04477" cy="6.04474" r="5.82886" stroke="url(#paint0_linear_516_1969)" strokeWidth="0.431767"/>
                                <circle cx="6.04477" cy="6.04454" r="4.31767" fill="url(#paint1_linear_516_1969)"/>
                                <defs>
                                  <linearGradient id="paint0_linear_516_1969" x1="9.81685" y1="47.9365" x2="-2.98762" y2="47.1248" gradientUnits="userSpaceOnUse">
                                    <stop stopColor="#7928CA"/>
                                    <stop offset="1" stopColor="#FF0080"/>
                                  </linearGradient>
                                  <linearGradient id="paint1_linear_516_1969" x1="8.73912" y1="35.9673" x2="-0.406936" y2="35.3874" gradientUnits="userSpaceOnUse">
                                    <stop stopColor="#7928CA"/>
                                    <stop offset="1" stopColor="#FF0080"/>
                                  </linearGradient>
                                </defs>
                              </svg>
                            ) : (
                              <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="6.04474" cy="6.04474" r="5.82886" stroke="#667085" strokeWidth="0.431767"/>
                              </svg>
                            )}
                            <SoftTypography
                              sx={{
                                fontWeight: 600,
                                fontSize: "15.54px",
                                lineHeight: "23px",
                                letterSpacing: "-0.02em",
                                color: "#475467",
                              }}
                            >
                              {item.label}
                            </SoftTypography>
                          </li>
                        ))}
                      </ul>
                  </SoftBox>
                )}
              </SoftBox>
              {/* Calendar button for desktop view */}
              {section !== "list" && (
              <SoftBox
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <SoftButton
                  sx={{
                    padding: "13px, 25px, 13px, 25px",
                    alignContent: "center",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: isSmallScreen ? "0.9rem" : "1.25rem",
                    minWidth: "max-content",
                    minHeight: "max-content",
                    width: "max-content",
                    height: "max-content",
                    gap: "0.25rem",
                    display: { mini: "none", xs: "flex" },
                  }}
                  onClick={() => navigate("/calendar")}
                >
                  <CalendarMonthIcon
                    sx={{ height: "1.25rem", color: "primary.main" }}
                  />
                </SoftButton>
              </SoftBox>
              )}
              {isAdmin && section !== "list" && (
                <SoftButton
                  onClick={() => navigate("/settings/players")}
                  color="primary"
                  sx={{
                    padding: "13px, 25px, 13px, 25px",
                    alignContent: "center",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: "max-content",
                    minHeight: "max-content",
                    width: "max-content",
                    height: "max-content",
                    gap: "0.25rem",
                    display: { mini: "none", xs: "flex" },
                    fontSize: "14px",
                    fontWeight: "500",
                    textTransform: "capitalize",
                    ...(screenWidth === 1024 && ({ 
                      fontSize: "12px!important",
                      padding: "10px!important"
                      }))
                  }}
                >
                  <PersonAddIcon sx={{ height: "1.25rem", color: "primary", }}/> Add Players
                </SoftButton>
              )}

              {/* Standalone list: compact Add Players lives on the control
                  row, right beside the KPI filter — one aligned cluster.
                  isAdmin only (not isRealAdmin) — hidden while previewing
                  in player mode, same as everything else in that view. */}
              {section === "list" && isAdmin && (
                <SoftButton
                  onClick={() => navigate("/settings/players")}
                  color="primary"
                  sx={{
                    height: "30px",
                    minHeight: "30px",
                    padding: "0 12px",
                    borderRadius: "8px",
                    fontSize: "12px",
                    fontWeight: 500,
                    textTransform: "capitalize",
                    minWidth: "max-content",
                    gap: "0.25rem",
                    flexShrink: 0,
                  }}
                >
                  <PersonAddIcon sx={{ height: "1rem", color: "primary" }} /> Add Players
                </SoftButton>
              )}

              {/** Mobile add player button */}
              {(!isAdmin && isRealAdmin) && section !== "list" && (
                <SoftButton
                  onClick={() => navigate("/settings/players")}
                  color="primary"
                  sx={{
                    padding: "13px, 25px, 13px, 25px",
                    alignContent: "center",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: "max-content",
                    minHeight: "max-content",
                    width: "max-content",
                    height: "max-content",
                    gap: "0.25rem",
                    display: { mini: "none", xs: "flex" },
                    fontSize: "14px",
                    fontWeight: "500",
                    textTransform: "capitalize",
                  }}
                >
                  <PersonAddIcon sx={{ height: "1.25rem", color: "primary", }}/>
                </SoftButton>
              )}
            </Grid>
          )}
        </Grid>

        {section !== "list" && (
          (isViewOnly || isSmallScreen)
            ? <Divider sx={{ marginY: { mini: "1rem", lg: "1.5rem" } }} />
            : <hr className="my-[0.75rem] divide-slate-300" />
        )}

        {isSmallScreen && !isViewOnly && (
          <Grid
            item
            mini={12}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              width: '100%',
              gap: '0.5rem',
              flexWrap: 'wrap',
              rowGap: '0.5rem',
              // The "list" section skips the hr/Divider spacer above (it owns
              // its own tighter header), so without this the sticky header's
              // own bottom border sits flush against — and visually cuts
              // through — this row's icon buttons.
              marginTop: section === "list" ? "16px" : 0,
            }}
          >
            {/* Mobile filter — icon-only KPI select (funnel). Drops the
                "Filter by KPI:" label + wide control that collided on a phone.
                Menu still shows the full labelled options. Desktop is a separate
                block and is untouched. */}
            {leaderboardTab === 0 && (
              <SoftBox
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <LightSelect
                  iconOnly
                  menuWidth={200}
                  icon={(
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 5h16M7 12h10M10 19h4" /></svg>
                  )}
                  options={kpiOptions}
                  value={toggleKpi}
                  onChange={(option) => setToggleKpi(option.value)}
                  height={38}
                  radius={8}
                />
              </SoftBox>
            )}
            <SoftBox
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{maxWidth: "140px!important"}}
            >
              {leaderboardTab === 0 && (
                <LightSelect
                  iconOnly
                  menuWidth={190}
                  icon={(
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="17" rx="2" /><path d="M3 9h18M8 2v4M16 2v4" /></svg>
                  )}
                  options={togglePeriodNames.map((p) => ({ value: p.key, label: p.label }))}
                  value={togglePeriod}
                  onChange={(option) => setTogglePeriod(option.value)}
                  height={38}
                  radius={8}
                />
              )}
              {leaderboardTab === 1 && (
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    openTo="month"
                    views={["year", "month"]}
                    inputFormat="MM/YYYY"
                    mask="__/____"
                    value={monthRef}
                    onChange={(newValue) => setMonthRef(newValue)}
                    components={{
                      OpenPickerIcon: CalendarOpenPickerIcon,
                    }}
                    PopperProps={{
                    sx: {
                      '& .MuiPaper-root': {
                        borderRadius: "11.2px",
                        maxWidth: "190px",
                        width: "190px",
                        marginTop: "1rem",
                        marginLeft: "4rem"
                      },
                      '& .MuiYearPicker-root': {
                        borderRadius: "11.2px",
                        maxWidth: "190px",
                        width: "190px",
                      },
                      '& .PrivatePickersYear-yearButton': {
                        fontFamily: 'DM Sans',
                        fontWeight: 600,
                        fontSize: "12px",
                        lineHeight: "18px",
                        letterSpacing: "-0.02em",
                        height: "auto !important",
                        width: "fit-content !important",
                        maxWidth: "none !important",
                        paddingRight: "6px",
                        paddingLeft: "6px"
                      },
                      '& .PrivatePickersYear-selected': {
                        backgroundColor: `${primaryColor} !important`,
                      },
                      '& .MuiMonthPicker-root': {
                        maxWidth: "180px",
                        width: "180px",
                      },
                      '& .MuiTypography-subtitle1': {
                        fontFamily: 'DM Sans',
                        fontWeight: 600,
                        fontSize: "12px",
                        lineHeight: "18px",
                        letterSpacing: "-0.02em",
                        height: "auto !important",
                        width: "fit-content !important",
                        maxWidth: "none !important"
                      },
                      '& .MuiTypography-h5': {
                        fontFamily: 'DM Sans',
                        fontWeight: 600,
                        fontSize: "12px",
                        lineHeight: "18px",
                        letterSpacing: "-0.02em",
                        width: "fit-content !important",
                        maxWidth: "none !important",
                        backgroundColor: `${primaryColor} !important`,
                        height: "auto !important"
                      },
                    },
                  }}
                    slotProps={{
                      textField: {
                        helperText: null,
                        label: undefined,
                        sx: {
                          width: "10rem",
                          "& .MuiInputBase-root": {
                            borderRadius: "4px!important",
                          },
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "4px!important",
                          },
                          "& .MuiInputBase-input": {
                            fontFamily: 'DM Sans',
                            fontWeight: 600,
                            fontSize: "12px",
                            lineHeight: "18px",
                            letterSpacing: "-0.02em",
                            color: "#667085",
                          },
                          "& .MuiFormLabel-root": {
                            fontFamily: 'DM Sans',
                            fontWeight: 600,
                            fontSize: "12px",
                            lineHeight: "18px",
                            letterSpacing: "-0.02em",
                            color: "#667085",
                          },
                          "& svg": {
                            width: "21px",
                            height: "21px",
                            marginRight: "-1rem!important"
                          },
                        }
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        helperText={null}
                        sx={{
                          width: "10rem",
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "4px!important",
                          },
                          "& .MuiInputBase-input": {
                            fontFamily: 'DM Sans',
                            fontWeight: 600,
                            fontSize: "12px",
                            lineHeight: "18px",
                            letterSpacing: "-0.02em",
                            color: "dark.main",
                          },
                          "& .MuiFormLabel-root": {
                            fontFamily: 'DM Sans',
                            fontWeight: 600,
                            fontSize: "12px",
                            lineHeight: "18px",
                            letterSpacing: "-0.02em",
                            color: "#667085",
                          },
                          "& svg": {
                            width: "21px",
                            height: "21px",
                            marginRight: "-1rem!important"
                          },
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>
              )}
              {isToggle && (
                <SoftBox 
                  sx={{
                    marginTop: "16rem",
                    marginLeft: "3rem",
                    zIndex: "999",
                    background: "var(--tvd-card, #FFFFFF)!important", 
                    maxWidth: "193px", 
                    width: "193px", 
                    paddingX: "0.5rem",
                    paddingY: "0.25rem", 
                    position: "absolute",
                    border: "1.3px solid rgba(0, 0, 0, 0.1)",
                    boxShadow: "0px 20.7px 31px rgba(0, 0, 0, 0.1)",
                    borderRadius: "10.4px",
                  }}>
                    <SoftTypography
                      sx={{
                        fontStyle: "normal",
                        fontWeight: 500,
                        fontSize: "10.4px",
                        lineHeight: "23px",
                        color: "#667085",
                      }}>
                      Quick date filter
                    </SoftTypography>
                    <ul style={{ fontSize: "15.5px", color: "#667085", marginBottom: "1rem" }}>
                      {togglePeriodNames.map((item) => (
                        <li
                          key={item.key}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                            padding: "0.5rem",
                            cursor: "pointer",
                            background: togglePeriod === item.key ? "#EAECF0" : "transparent",
                            borderRadius: "6px",
                            borderBottom: togglePeriod === item.key ? "0.4px solid #98A2B3" : "none!important"

                          }}
                          onClick={() => {
                            setTogglePeriod(item.key);
                            setisToggle(false);
                          }}
                        >
                          {togglePeriod === item.key ? (
                            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <circle cx="6.04477" cy="6.04474" r="5.82886" stroke="url(#paint0_linear_516_1969)" strokeWidth="0.431767"/>
                              <circle cx="6.04477" cy="6.04454" r="4.31767" fill="url(#paint1_linear_516_1969)"/>
                              <defs>
                                <linearGradient id="paint0_linear_516_1969" x1="9.81685" y1="47.9365" x2="-2.98762" y2="47.1248" gradientUnits="userSpaceOnUse">
                                  <stop stopColor="#7928CA"/>
                                  <stop offset="1" stopColor="#FF0080"/>
                                </linearGradient>
                                <linearGradient id="paint1_linear_516_1969" x1="8.73912" y1="35.9673" x2="-0.406936" y2="35.3874" gradientUnits="userSpaceOnUse">
                                  <stop stopColor="#7928CA"/>
                                  <stop offset="1" stopColor="#FF0080"/>
                                </linearGradient>
                              </defs>
                            </svg>
                          ) : (
                            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <circle cx="6.04474" cy="6.04474" r="5.82886" stroke="#667085" strokeWidth="0.431767"/>
                            </svg>
                          )}
                          <SoftTypography
                            sx={{
                              fontWeight: 600,
                              fontSize: "15.54px",
                              lineHeight: "23px",
                              letterSpacing: "-0.02em",
                              color: "#475467",
                            }}
                          >
                            {item.label}
                          </SoftTypography>
                        </li>
                      ))}
                    </ul>
                </SoftBox>
              )}
            </SoftBox>
            {/* Calendar button for desktop view — the standalone dashboard
                widget (section="list") has no use for a "go to /calendar"
                shortcut crammed into an already-tight control row, and the
                desktop layout above already hides it for that section. */}
            {section !== "list" && (
              <SoftBox
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <SoftButton
                  sx={{
                    padding: "13px, 25px, 13px, 25px",
                    alignContent: "center",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: isSmallScreen ? "0.9rem" : "1.25rem",
                    minWidth: "max-content",
                    minHeight: "max-content",
                    width: "max-content",
                    height: "max-content",
                    gap: "0.25rem",
                    display: "flex",
                  }}
                  onClick={() => navigate("/calendar")}
                >
                  <CalendarMonthIcon
                    sx={{ height: "1.25rem", color: "primary.main" }}
                  />
                </SoftButton>
              </SoftBox>
            )}

            {/* Standalone list widget: compact Add Players, same treatment
                as the desktop control row above — keeps it from dominating
                the narrow mobile card next to the filter icons.
                isAdmin only (not isRealAdmin) — hidden while previewing
                in player mode, same as everything else in that view. */}
            {section === "list" && isAdmin && (
              <SoftButton
                onClick={() => navigate("/settings/players")}
                color="primary"
                sx={{
                  height: "30px",
                  minHeight: "30px",
                  padding: "0 12px",
                  borderRadius: "8px",
                  fontSize: "12px",
                  fontWeight: 500,
                  textTransform: "capitalize",
                  minWidth: "max-content",
                  gap: "0.25rem",
                  flexShrink: 0,
                  marginLeft: "auto",
                }}
              >
                <PersonAddIcon sx={{ height: "1rem", color: "primary" }} /> Add Players
              </SoftButton>
            )}

            {isAdmin && section !== "list" && (
              <SoftButton
                onClick={() => navigate("/settings/players")}
                color="primary"
                sx={{
                  padding: "13px, 25px, 13px, 25px",
                  alignContent: "center",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: "max-content",
                  minHeight: "max-content",
                  width: "max-content",
                  height: "max-content",
                  gap: "0.25rem",
                  display: "flex",
                  fontSize: "14px",
                  fontWeight: "500",
                  textTransform: "capitalize",
                  ...(screenWidth === 1024 && ({
                    fontSize: "12px!important",
                    padding: "10px!important"
                    }))
                }}
              >
                <PersonAddIcon sx={{ height: "1.25rem", color: "primary", }}/> Add Players
              </SoftButton>
            )}

            {/** Mobile add player button */}
            {(!isAdmin && isRealAdmin) && section !== "list" && (
              <SoftButton
                onClick={() => navigate("/settings/players")}
                color="primary"
                sx={{
                  padding: "13px, 25px, 13px, 25px",
                  alignContent: "center",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: "max-content",
                  minHeight: "max-content",
                  width: "max-content",
                  height: "max-content",
                  gap: "0.25rem",
                  display: "flex",
                  fontSize: "14px",
                  fontWeight: "500",
                  textTransform: "capitalize",
                }}
              >
                <PersonAddIcon sx={{ height: "1.25rem", color: "primary", }}/>
              </SoftButton>
            )}
          </Grid>
        )}

        <SoftBox>
          {/* {leaderboardTab === 0 && !isViewOnly && !isMidsizeScreen && (
            <SoftBox
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <SoftSelect
                id="periods"
                name="periods"
                options={PERIODS}
                onChange={(option) => setTogglePeriod(option.value)}
                defaultValue={PERIODS[3]}
              />
            </SoftBox>
          )} */}

          {LeaderboardComponent[leaderboardTab]}
        </SoftBox>
      </SoftBox>
    </SoftBox>
  );
}

export default LeaderBoard;
